// Scorecards' Bright Pattern email entry (2026-11-02, Patrick's ask): paste
// the Power BI weekly or monthly email results for the Support team, one line
// per agent and period. My Stats blends them with each agent's CXHub
// contacts into a Combined Email AHT. Collapsed by default; it is a monthly
// chore, not part of reading the tab.
import { useEffect, useState } from 'react'
import { Box, Button, Collapse, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Alert } from '@mui/material'
import { fetchBpEmailStats, saveBpEmailStats, deleteBpEmailStat } from '../api.js'

const PLACEHOLDER = `2026-10-31, amalerbi@1aauto.com, 212, 00:04:04
2026-10, amalerbi@1aauto.com, 1079, 00:04:07`

function mmss(seconds) {
  const m = Math.floor(seconds / 60)
  return `${m}:${String(seconds % 60).padStart(2, '0')}`
}

export default function BpEmailStatsPanel() {
  const [open, setOpen] = useState(false)
  const [rows, setRows] = useState(null)
  const [text, setText] = useState('')
  const [errors, setErrors] = useState([])
  const [message, setMessage] = useState(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open || rows) return
    fetchBpEmailStats().then((r) => setRows(r.rows)).catch((err) => setErrors([err.message]))
  }, [open, rows])

  async function save() {
    setBusy(true)
    setErrors([])
    setMessage(null)
    try {
      const r = await saveBpEmailStats(text)
      setRows(r.rows)
      setText('')
      setMessage(`Saved ${r.saved} row${r.saved === 1 ? '' : 's'}.`)
    } catch (err) {
      setErrors(err.body?.errors ?? [err.message])
    } finally {
      setBusy(false)
    }
  }

  async function remove(row) {
    setErrors([])
    setMessage(null)
    try {
      const r = await deleteBpEmailStat(row)
      setRows(r.rows)
    } catch (err) {
      setErrors([err.message])
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>Bright Pattern email (from Power BI)</Typography>
        <Button size="small" onClick={() => setOpen((o) => !o)}>{open ? 'Hide' : 'Enter or review'}</Button>
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 1 }}>
          One line per agent and period: period, agent email, emails, Email AHT. A week is its ending Saturday
          (2026-10-31), a month is 2026-10. Saving a line that already exists replaces it. Agents see these on My Stats
          as BP Emails, BP Email AHT and a Combined Email AHT with their CXHub contacts (Support Email included).
          My Stats can take up to an hour to show a change.
        </Typography>
        <TextField
          multiline minRows={4} fullWidth size="small" value={text} placeholder={PLACEHOLDER}
          onChange={(e) => setText(e.target.value)}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button variant="contained" size="small" disabled={busy || !text.trim()} onClick={save}>Save</Button>
        </Stack>
        {message && <Alert severity="success" sx={{ mt: 1 }}>{message}</Alert>}
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Nothing was saved.
            {errors.map((e) => <div key={e}>{e}</div>)}
          </Alert>
        )}
        {rows && rows.length > 0 && (
          <Box sx={{ mt: 2, maxHeight: 320, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell align="right">Emails</TableCell>
                  <TableCell align="right">Email AHT</TableCell>
                  <TableCell>Entered by</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.granularity}|${r.period}|${r.agentEmail}`} hover>
                    <TableCell>{r.period}{r.granularity === 'month' ? ' (month)' : ''}</TableCell>
                    <TableCell>{r.agentEmail}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{r.emails}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{mmss(r.ahtSeconds)}</TableCell>
                    <TableCell>{r.enteredBy}</TableCell>
                    <TableCell padding="none">
                      <Button size="small" color="error" onClick={() => remove(r)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        {rows && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Nothing entered yet.</Typography>
        )}
      </Collapse>
    </Paper>
  )
}
